// Admin chrome is intentionally fixed to the business's Central timezone.
// This is a display-only convention: API values and range boundaries remain UTC.
export const ADMIN_TIME_ZONE = 'America/Chicago';

/**
 * cas-39395 (Ben × Daniel 2026-10-16: Central for admin, "CT" everywhere): the
 * short, DST-free label admin shows for a zone. US zones read CT/ET/PT/MT; any
 * other zone keeps its Intl short name. Empty for no zone.
 */
const ADMIN_ZONE_LABELS: Readonly<Record<string, string>> = {
  'America/Chicago': 'CT',
  'America/New_York': 'ET',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT'
};
export function adminZoneLabel(timeZone: string | null | undefined, at: Date = new Date()): string {
  if (!timeZone) return '';
  const known = ADMIN_ZONE_LABELS[timeZone];
  if (known) return known;
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
        .formatToParts(at)
        .find(part => part.type === 'timeZoneName')?.value ?? ''
    );
  } catch {
    return '';
  }
}

// Matches a bare date-only string "YYYY-MM-DD" (no time component).
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

type DateOnlyParts = { year: number; month: number; day: number };

const dateOnlyParts = (value: string | number | Date | null | undefined): DateOnlyParts | null => {
  if (typeof value !== 'string') return null;
  const match = DATE_ONLY_RE.exec(value);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
};

const parseDateValue = (value: string | number | Date | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const normalized = value < 1_000_000_000_000 ? value * 1000 : value;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  // A bare "YYYY-MM-DD" is parsed by `new Date()` as UTC midnight, which then
  // rolls back a calendar day when rendered in any UTC-negative timezone.
  // Construct a LOCAL-midnight date from the parts so the backend's date-only
  // bucket retains its true calendar day for validation and callers that need
  // the parsed Date. Date-only formatters below use the parts directly so a
  // Central conversion cannot move that calendar day across midnight.
  // Full ISO timestamps (with a time component) fall through to `new Date()`
  // so they represent the actual instant that will be rendered in Central.
  const dateOnly = DATE_ONLY_RE.exec(value);
  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const month = Number(dateOnly[2]);
    const day = Number(dateOnly[3]);
    const parsed = new Date(year, month - 1, day);
    // DATE_ONLY_RE matches the SHAPE, not the calendar range — `new Date(2026, 1, 30)`
    // silently rolls over to Mar 2. Round-trip the parts so malformed dates
    // ("2026-02-30", "2026-13-45") return null (→ "-") instead of a wrong date.
    if (
      Number.isNaN(parsed.getTime()) ||
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }
    return parsed;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatAdminDate = (value: string | number | Date | null | undefined): string => {
  return formatAdminValue(value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatAdminDateTime = (value: string | number | Date | null | undefined): string => {
  return formatAdminValue(value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

export const formatAdminTime = (value: string | number | Date | null | undefined): string =>
  formatAdminValue(value, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

/**
 * cas-89f6: a timestamp in a named reporting zone, with that zone's marker
 * ("Oct 9, 2026, 02:16 PM PDT"). For views whose caption names their zone,
 * such as the analytics "All times PDT" cards. Without a zone it falls back to
 * the Central admin chrome format.
 */
export const formatAdminDateTimeInZone = (
  value: string | number | Date | null | undefined,
  timeZone: string | null | undefined
): string => {
  if (!timeZone) return formatAdminDateTime(value);
  const parsed = parseDateValue(value);
  if (!parsed) return '-';
  try {
    const text = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone
    }).format(parsed);
    const label = adminZoneLabel(timeZone, parsed);
    return label ? `${text} ${label}` : text;
  } catch {
    return formatAdminDateTime(value);
  }
};

/** Format a Central civil-time schedule without treating the hour as viewer-local. */
export const formatAdminScheduleTime = (hour: number, minute: number, at = new Date()): string => {
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return '-';
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return '-';
  const clock = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC'
  }).format(new Date(Date.UTC(2000, 0, 1, hour, minute)));
  return `${clock} ${zoneAbbreviation(at)}`;
};

export const formatDateCompact = (value: string | number | Date | null | undefined): string => {
  return formatAdminValue(value, {
    month: 'short',
    day: 'numeric',
    year: '2-digit'
  });
};

export const formatAdminMonthDay = (value: string | number | Date | null | undefined): string => {
  return formatAdminValue(value, {
    month: 'short',
    day: 'numeric'
  });
};

const zoneAbbreviation = (date: Date): string => adminZoneLabel(ADMIN_TIME_ZONE, date) || 'CT';

/**
 * Centralizes the visible admin timestamp contract. Date-only values are
 * calendar labels, not instants: format them at UTC noon with the Central
 * abbreviation calculated for that date so "2026-07-06" remains Jul 6 even
 * when the viewer is outside Central. Full timestamps are converted by the
 * IANA timezone identifier and include the DST-aware CST/CDT marker.
 *
 * Deliberate semantic-timezone views (for example Continue Path's recipient
 * schedule and analytics' reporting window) must keep their own timezone and
 * label it at their call site; this helper is for admin display chrome only.
 */
function formatAdminValue(
  value: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions
): string {
  const parsed = parseDateValue(value);
  if (!parsed) return '-';

  const parts = dateOnlyParts(value);
  if (parts) {
    // Noon UTC is safely within the same Central calendar day for all current
    // Central offsets and lets us use UTC solely to preserve the date label.
    const calendarDate = new Date(
      Date.UTC(parts.year, parts.month - 1, parts.day, options.hour === undefined ? 12 : 0)
    );
    const zoneDate = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
    const calendarOptions = { ...options, timeZone: 'UTC' } satisfies Intl.DateTimeFormatOptions;
    return `${new Intl.DateTimeFormat('en-US', calendarOptions).format(calendarDate)} ${zoneAbbreviation(zoneDate)}`;
  }

  return `${new Intl.DateTimeFormat('en-US', {
    ...options,
    timeZone: ADMIN_TIME_ZONE
  }).format(parsed)} ${zoneAbbreviation(parsed)}`;
}
